import { z } from "zod";

export type OperationParamsSchema = z.ZodType<
  Record<string, unknown>,
  z.ZodTypeDef,
  Record<string, unknown>
>;

export type StepType =
  | "image"
  | "video"
  | "tts"
  | "stt"
  | "text"
  | "publish"
  | "dialogue"
  | "alignment"
  | "music"
  | "audio-enhance"
  | "voice-extract";
export type ProviderName =
  | "runware"
  | "fal"
  | "kie"
  | "openrouter"
  | "elevenlabs"
  | "openai"
  | "muapi"
  | "xai"
  | "kling"
  | "vertex"
  | "comfyui"
  | "replicate";

export type BaseOptionItem = {
  label: string;
  value: string;
  icon?: string;
  description?: string;
};
export type OptionWithItems<TConfig = Record<string, unknown>> = TConfig & {
  items: readonly BaseOptionItem[];
};
export type OptionConfigOnly<TConfig = Record<string, unknown>> = TConfig;

export type AttachmentType = "image" | "video" | "audio" | "media";

export type AttachmentSpec = {
  type: AttachmentType;
  label: string;
  slot: string;
  required?: boolean;
  group?: string;
  allowedFormats?: string[];
  maxSizeInBytes?: number;
};

export const STEP_FEATURES = [
  "aspectRatio",
  "duration",
  "negativePrompt",
  "imageSize",
  "imageFile",
  "videoFile",
  "referenceImages",
  "resolution",
  "attachments",
  "prompts",
  "variations",
  "syncMode",
  "upscaleFactor",
  "targetFps",
  "codec",
  "quality",
  "background",
  "characters",
  "characterOrientation",
  "terminalOutput",
  "subjectIsPerson",
  "bgRemovalModel",
  "multiPrompt",
] as const;

export type StepFeatureKey = (typeof STEP_FEATURES)[number];
export type StepFeatureMap = Partial<Record<StepFeatureKey, true>>;

export type FeatureOptionTypeMap = {
  aspectRatio: OptionWithItems;
  duration: OptionWithItems;
  imageSize: OptionWithItems;
  resolution: OptionWithItems;
  voice: OptionWithItems;
  syncMode: OptionWithItems;
  upscaleFactor: OptionWithItems;
  targetFps: OptionWithItems;
  codec: OptionWithItems;
  quality: OptionWithItems;
  background: OptionWithItems;
  characterOrientation: OptionWithItems;
  subjectIsPerson: OptionWithItems;
  bgRemovalModel: OptionWithItems;
  negativePrompt: OptionConfigOnly<{
    maxLength?: number;
    placeholder?: string;
  }>;
  imageFile: OptionConfigOnly<{
    maxFiles?: number;
    maxSizeInBytes?: number;
    allowedFormats?: string[];
  }>;
  videoFile: OptionConfigOnly<{
    maxFiles?: number;
    maxSizeInBytes?: number;
    allowedFormats?: string[];
  }>;
  referenceImages: OptionConfigOnly<{
    maxFiles?: number;
    maxSizeInBytes?: number;
    allowedFormats?: string[];
  }>;
  attachments: OptionConfigOnly<readonly AttachmentSpec[]>;
};

export type StepOptions<TFeatures extends StepFeatureMap = StepFeatureMap> = {
  [K in keyof TFeatures & keyof FeatureOptionTypeMap]?: FeatureOptionTypeMap[K];
} & Partial<FeatureOptionTypeMap>;

// ---------- Existing payloads (Fal / Runware) ----------

export type FalImagePayload = {
  model: string;
  prompt: string;
  width?: number;
  height?: number;
  [key: string]: unknown;
};

export type FalVideoPayload = {
  model: string;
  prompt?: string;
  videoMediaID?: string;
  imageMediaID?: string | null;
  resolution?: "480p" | "580p" | "720p" | "1080p";
  video_url?: string;
  audio_url?: string;
  sync_mode?: "cut_off" | "loop" | "bounce" | "silence" | "remap";
  keep_audio?: boolean;
  image_url?: string;
  image_urls?: string[];
  elements?: (
    | {
        frontal_image_url: string;
        reference_image_urls: string[];
        voice_id?: string;
      }
    | { video_url: string; voice_id?: string }
  )[];
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  duration?: string; // Duration in seconds - varies by model (e.g., "5", "10", "3"-"15")
  // Kling v3 specific fields
  start_image_url?: string;
  end_image_url?: string;
  generate_audio?: boolean;
  negative_prompt?: string;
  cfg_scale?: number;
  // Kling v3 multi-prompt (scene splitting)
  multi_prompt?: { prompt: string; duration?: string }[];
  shot_type?: "customize";
  voice_ids?: string[];
  [key: string]: unknown;
};

export type RunwareImagePayload = {
  model: string;
  positivePrompt: string;
  width?: number;
  height?: number;
  negativePrompt?: string;
  referenceImages?: string[];
  providerSettings?: unknown;
  outputFormat?: "JPG" | "PNG" | "WEBP";
};

export type RunwareVideoPayload = {
  model: string;
  positivePrompt: string;
  width?: number;
  height?: number;
  duration?: number | "auto";
  negativePrompt?: string;
  CFGScale?: number;
  resolution?: "480p" | "720p";
  /** Structured inputs for frame images, motion control, and I2V. */
  inputs?: {
    referenceImages?: string[];
    referenceVideos?: string[];
    /** Frame images as URL strings. 1 image = start frame, 2 = start + end. */
    frameImages?: string[];
  };
  /** Top-level settings. Seedance 2 takes `audio` here, not in providerSettings. */
  settings?: {
    audio?: boolean;
  };
  providerSettings?: {
    bytedance?: {
      audio?: boolean;
      cameraFixed?: boolean;
    };
    klingai?: {
      /** Enable native audio generation (dialogue, SFX, ambience). */
      sound?: boolean;
      /** Preserve original audio from reference video. */
      keepOriginalSound?: boolean;
      /** Character orientation for motion control: "image" or "video". */
      characterOrientation?: "image" | "video";
      /** Multi-prompt scene segments (up to 6). */
      multiPrompt?: { prompt: string; duration?: string }[];
      /** Shot type when using multi-prompt. */
      shotType?: "customize" | "intelligence";
    };
  };
};

export type RunwareVectorizePayload = {
  model: "recraft:1@1" | "picsart:1@1";
  outputFormat: "SVG";
  inputs: {
    image: string;
  };
};

export type OpenAiSoraVideoModel = "sora-2" | "sora-2-pro";

export type OpenAiSoraVideoSize =
  | "720x1280"
  | "1280x720"
  | "1024x1792"
  | "1792x1024"
  | "1080x1920"
  | "1920x1080";

export type OpenAiSoraVideoSeconds = "4" | "8" | "12" | "16" | "20";

export type OpenAiSoraInputReference = {
  file_id?: string;
  image_url?: string;
};

export type OpenAiSoraVideoPayload = {
  model: OpenAiSoraVideoModel;
  prompt: string;
  size: OpenAiSoraVideoSize;
  seconds: OpenAiSoraVideoSeconds;
  input_reference?: OpenAiSoraInputReference;
  characters?: { id: string }[];
};

// ---------- OpenAI (GPT Image 2) ----------

export type OpenAiImageModel = "gpt-image-2";

export type OpenAiImageSize = "1024x1024" | "1536x1024" | "1024x1536";

export type OpenAiImageQuality = "auto" | "low" | "medium" | "high";

export type OpenAiImageOutputFormat = "png" | "jpeg" | "webp";

export type OpenAiImagePayload = {
  model: OpenAiImageModel;
  prompt: string;
  size: OpenAiImageSize;
  quality: OpenAiImageQuality;
  output_format: OpenAiImageOutputFormat;
  n?: number;
  /**
   * Reference image URLs. When present, the runtime routes to
   * POST /v1/images/edits (multipart) instead of /v1/images/generations
   * and uploads these as "image" form parts.
   */
  referenceImages?: string[];
};

// ---------- New: Kie (Sora 2 Pro Storyboard) ----------

/**
 * Payload shape per Kie docs:
 * POST /api/v1/jobs/createTask
 * {
 *   "model": "sora-2-pro-storyboard",
 *   "callBackUrl": "https://your-domain.com/cb", // optional
 *   "input": {
 *     "n_frames": "10" | "15" | "25",
 *     "aspect_ratio": "portrait" | "landscape",
 *     "image_urls": [ "https://..." ], // optional (0..1 typical)
 *     "shots": [{ "Scene": string, "duration": number }]
 *   }
 * }
 */
export type KieStoryboardCreateTaskPayload = {
  model: "sora-2-pro-storyboard";
  callBackUrl?: string;
  input: {
    n_frames: "10" | "15" | "25"; // NOTE: string, not number
    aspect_ratio?: "portrait" | "landscape";
    image_urls?: string[];
    shots: { Scene: string; duration: number }[];
  };
};

// ---------- New: Kie (Sora 2 Pro Image to Video) ----------

/**
 * Payload shape per Kie docs:
 * POST /api/v1/jobs/createTask
 * {
 *   "model": "sora-2-pro-image-to-video",
 *   "callBackUrl": "https://your-domain.com/cb", // optional
 *   "input": {
 *     "prompt": "string",
 *     "image_urls": ["https://..."], // required, single image
 *     "aspect_ratio": "portrait" | "landscape",
 *     "n_frames": "10" | "15" | "25",
 *     "size": "high",
 *     "remove_watermark": boolean
 *   }
 * }
 */
export type KieImageToVideoCreateTaskPayload = {
  model: "sora-2-pro-image-to-video" | "sora-2-pro-text-to-video";
  callBackUrl?: string;
  input: {
    prompt: string;
    image_urls?: string[]; // optional, only for image-to-video
    aspect_ratio?: "portrait" | "landscape";
    n_frames: "10" | "15"; // NOTE: string, not number
    size: "standard" | "high";
    remove_watermark: boolean;
  };
};

export type KieSora2ImageToVideoCreateTaskPayload = {
  model: "sora-2-image-to-video" | "sora-2-text-to-video";
  callBackUrl?: string;
  input: {
    prompt: string;
    image_urls?: string[]; // optional, only for image-to-video
    aspect_ratio?: "portrait" | "landscape";
    n_frames: "10" | "15"; // NOTE: string, not number
    remove_watermark: boolean;
  };
};

// ---------- New: Kie (Nano Banana Pro - Image) ----------

/**
 * Payload shape per Kie docs:
 * POST /api/v1/jobs/createTask
 * {
 *   "model": "nano-banana-pro",
 *   "callBackUrl": "https://your-domain.com/cb", // optional
 *   "input": {
 *     "prompt": "string",
 *     "image_input": ["https://..."], // optional, up to 8 images
 *     "aspect_ratio": "1:1" | "2:3" | "3:2" | ... ,
 *     "resolution": "1K" | "2K" | "4K",
 *     "output_format": "png" | "jpg"
 *   }
 * }
 */
export type KieNanoBananaProCreateTaskPayload = {
  model: "nano-banana-pro";
  callBackUrl?: string;
  input: {
    prompt: string;
    image_input?: string[]; // optional, up to 8 reference images
    aspect_ratio?:
      | "1:1"
      | "2:3"
      | "3:2"
      | "3:4"
      | "4:3"
      | "4:5"
      | "5:4"
      | "9:16"
      | "16:9"
      | "21:9";
    resolution?: "1K" | "2K" | "4K";
    output_format?: "png" | "jpg";
  };
};

// ---------- Kie (Nano Banana 2 - Image) ----------

export type KieNanoBanana2CreateTaskPayload = {
  model: "nano-banana-2";
  callBackUrl?: string;
  input: {
    prompt: string;
    image_input?: string[]; // optional, up to 14 reference images
    aspect_ratio?:
      | "1:1"
      | "1:4"
      | "1:8"
      | "2:3"
      | "3:2"
      | "3:4"
      | "4:1"
      | "4:3"
      | "4:5"
      | "5:4"
      | "8:1"
      | "9:16"
      | "16:9"
      | "21:9";
    resolution?: "1K" | "2K" | "4K";
    output_format?: "png" | "jpg";
  };
};

// ---------- Kie (Gemini Omni Video) ----------

/**
 * Payload shape per Kie docs:
 * POST /api/v1/jobs/createTask
 * {
 *   "model": "gemini-omni-video",
 *   "callBackUrl": "https://your-domain.com/cb", // optional
 *   "input": {
 *     "prompt": "string",
 *     "duration": "4" | "6" | "8" | "10",
 *     "image_urls": ["https://..."], // optional, up to 7 reference images
 *     "aspect_ratio": "16:9" | "9:16",
 *     "resolution": "720p" | "1080p" | "4k"
 *   }
 * }
 */
export type KieGeminiOmniVideoCreateTaskPayload = {
  model: "gemini-omni-video";
  callBackUrl?: string;
  input: {
    prompt: string;
    duration: "4" | "6" | "8" | "10";
    image_urls?: string[];
    aspect_ratio?: "16:9" | "9:16";
    resolution?: "720p" | "1080p" | "4k";
  };
};

// ---------- New: Kie (Suno Music Generation) ----------

/**
 * Payload shape per KIE Suno docs:
 * POST /api/v1/generate
 * {
 *   "prompt": "A calm and relaxing piano track with soft melodies",
 *   "model": "V5",
 *   "instrumental": true,
 *   "customMode": false,
 *   "callBackUrl": "https://our-api/api/kie-webhook"
 * }
 */
export type SunoGeneratePayload = {
  prompt: string;
  model: "V4" | "V4_5" | "V4_5PLUS" | "V4_5ALL" | "V5";
  instrumental: boolean;
  customMode: boolean;
  callBackUrl?: string;
};

// ---------- Replicate (Seedance 2.0 via Replicate) ----------

export type ReplicateVideoPayload = {
  model: "bytedance/seedance-2.0" | "bytedance/seedance-2.0-fast";
  input: {
    prompt?: string;
    image?: string;
    last_frame_image?: string;
    reference_images?: string[];
    reference_videos?: string[];
    reference_audios?: string[];
    resolution: "720p" | "480p";
    duration: number;
    aspect_ratio: string;
    generate_audio?: boolean;
    seed?: number;
  };
};

type ReplicateVideoBinding<TParams extends OperationParamsSchema> = {
  provider: "replicate";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  estimateCostPerSecond?(p: BaseParams & z.infer<TParams>): number;
  costPerSecond: number;
  pricingUnit?: "per_second" | "per_generation";
  toPayload(p: BaseParams & z.infer<TParams>): ReplicateVideoPayload;
};

// ---------- Provider bindings ----------

type RunwareImageBinding<TParams extends OperationParamsSchema> = {
  provider: "runware";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): RunwareImagePayload;
};

type RunwareVectorizeBinding<TParams extends OperationParamsSchema> = {
  provider: "runware";
  modelId: "recraft:1@1" | "picsart:1@1";
  taskType: "vectorize";
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): RunwareVectorizePayload;
};

type RunwareVideoBinding<TParams extends OperationParamsSchema> = {
  provider: "runware";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  /** Optional parameter-aware per-second estimator for tiered pricing. */
  estimateCostPerSecond?(p: BaseParams & z.infer<TParams>): number;
  /** Cost per second in microdollars. Used for displaying unit cost. */
  costPerSecond: number;
  toPayload(p: BaseParams & z.infer<TParams>): RunwareVideoPayload;
};

type OpenAiSoraVideoBinding<TParams extends OperationParamsSchema> = {
  provider: "openai";
  modelId: OpenAiSoraVideoModel;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  /** Optional parameter-aware per-second estimator for tiered pricing. */
  estimateCostPerSecond?(p: BaseParams & z.infer<TParams>): number;
  /** Cost per second in microdollars. Used for displaying unit cost. */
  costPerSecond: number;
  toPayload(p: BaseParams & z.infer<TParams>): OpenAiSoraVideoPayload;
};

type OpenAiImageBinding<TParams extends OperationParamsSchema> = {
  provider: "openai";
  modelId: OpenAiImageModel;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): OpenAiImagePayload;
};

type FalImageBinding<TParams extends OperationParamsSchema> = {
  provider: "fal";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): FalImagePayload;
};

type FalVideoBinding<TParams extends OperationParamsSchema> = {
  provider: "fal";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  /** Optional parameter-aware per-second estimator for tiered pricing. */
  estimateCostPerSecond?(p: BaseParams & z.infer<TParams>): number;
  /** Cost per second in microdollars. Used for displaying unit cost. */
  costPerSecond: number;
  toPayload(p: BaseParams & z.infer<TParams>): FalVideoPayload;
};

// Kie binding for video models
type KieVideoBinding<TParams extends OperationParamsSchema> = {
  provider: "kie";
  modelId:
    | "sora-2-pro-storyboard"
    | "sora-2-pro-image-to-video"
    | "sora-2-image-to-video"
    | "gemini-omni-video";
  /** Return microdollars, same convention as others. */
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  /** Optional parameter-aware per-second estimator for tiered pricing. */
  estimateCostPerSecond?(p: BaseParams & z.infer<TParams>): number;
  /** Cost per second in microdollars. Used for displaying unit cost. */
  costPerSecond: number;
  toPayload(
    p: BaseParams & z.infer<TParams>,
  ):
    | KieStoryboardCreateTaskPayload
    | KieImageToVideoCreateTaskPayload
    | KieSora2ImageToVideoCreateTaskPayload
    | KieGeminiOmniVideoCreateTaskPayload;
};

// ---------- MuAPI (Seedance 2.0) ----------

export type MuapiVideoPayload = {
  endpoint:
    | "seedance-2-image-to-video"
    | "seedance-2-image-to-video-fast"
    | "seedance-2-text-to-video"
    | "seedance-2-text-to-video-fast"
    | "seedance-2-omni-reference-no-video"
    | "seedance-2-omni-reference-no-video-fast"
    | "seedance-v2.0-extend"
    | "seedance-v2.0-video-edit";
  prompt?: string;
  images_list?: string[];
  aspect_ratio?: string;
  duration?: number;
  quality?: "high" | "basic";
  request_id?: string;
  remove_watermark?: boolean;
  video_urls?: string[];
  video_files?: string[];
  audio_files?: string[];
  generate_audio?: boolean;
};

type MuapiVideoBinding<TParams extends OperationParamsSchema> = {
  provider: "muapi";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  estimateCostPerSecond?(p: BaseParams & z.infer<TParams>): number;
  costPerSecond: number;
  pricingUnit?: "per_second" | "per_generation";
  toPayload(p: BaseParams & z.infer<TParams>): MuapiVideoPayload;
};

// ---------- xAI (Grok Imagine) ----------

export type XaiImagePayload = {
  model: "grok-imagine-image" | "grok-imagine-image-pro";
  prompt: string;
  n?: number;
  aspect_ratio?: string;
  resolution?: "1k" | "2k";
  response_format?: "url" | "b64_json";
  // For image editing — reference image
  image?: { url: string; type: "image_url" };
};

export type XaiVideoPayload = {
  model: "grok-imagine-video";
  prompt: string;
  duration?: number;
  aspect_ratio?: string;
  resolution?: "480p" | "720p";
  image?: { url: string };
};

type XaiImageBinding<TParams extends OperationParamsSchema> = {
  provider: "xai";
  modelId: "grok-imagine-image" | "grok-imagine-image-pro";
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): XaiImagePayload;
};

type XaiVideoBinding<TParams extends OperationParamsSchema> = {
  provider: "xai";
  modelId: "grok-imagine-video";
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  estimateCostPerSecond?(p: BaseParams & z.infer<TParams>): number;
  costPerSecond: number;
  toPayload(p: BaseParams & z.infer<TParams>): XaiVideoPayload;
};

// ---------- Kling Direct API ----------

export type KlingDirectVideoPayload = {
  model_name: string;
  prompt: string;
  negative_prompt?: string;
  aspect_ratio?: string;
  duration?: string;
  mode?: string;
  cfg_scale?: number;
  callback_url?: string;
  // v2-master fields (existing — used for non-voice, non-element paths)
  image_url?: string;
  enable_audio?: boolean;
  // v3 fields (new — used when voice or elements are present)
  image?: string;
  image_tail?: string;
  sound?: "on" | "off";
  voice_list?: { voice_id: string; text?: string }[];
  element_list?: { element_id: string }[];
  elements?: (
    | { frontal_image: string; reference_images?: string[]; voice_id?: string }
    | { video_url: string; voice_id?: string }
  )[];
};

type KlingDirectVideoBinding<TParams extends OperationParamsSchema> = {
  provider: "kling";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  estimateCostPerSecond?(p: BaseParams & z.infer<TParams>): number;
  costPerSecond: number;
  toPayload(p: BaseParams & z.infer<TParams>): KlingDirectVideoPayload;
};

// Vertex AI video binding
type VertexImageRef = {
  gcsUri?: string;
  imageBytes?: string;
  mimeType?: string;
  /** HTTPS URL that runInference converts to imageBytes before calling the API. */
  url?: string;
};

type VertexVideoRef = {
  gcsUri?: string;
  videoBytes?: string;
  mimeType?: string;
  /** HTTPS URL that runInference converts to videoBytes before calling the API. */
  url?: string;
};

export type VertexVideoPayload = {
  model: string;
  prompt: string;
  config?: {
    aspectRatio?: string;
    durationSeconds?: number;
    pubsubTopic?: string;
    lastFrame?: VertexImageRef;
  };
  image?: VertexImageRef;
  video?: VertexVideoRef;
};

type VertexVideoBinding<TParams extends OperationParamsSchema> = {
  provider: "vertex";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  estimateCostPerSecond?(p: BaseParams & z.infer<TParams>): number;
  costPerSecond: number;
  toPayload(p: BaseParams & z.infer<TParams>): VertexVideoPayload;
};

// Kie binding for image models (Nano Banana Pro & Nano Banana 2)
type KieImageBinding<TParams extends OperationParamsSchema> = {
  provider: "kie";
  modelId: "nano-banana-pro" | "nano-banana-2";
  /** Return microdollars, same convention as others. */
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(
    p: BaseParams & z.infer<TParams>,
  ): KieNanoBananaProCreateTaskPayload | KieNanoBanana2CreateTaskPayload;
};

// ComfyUI binding for Lustify image models
export type ComfyUIImagePayload = {
  /** The full ComfyUI workflow JSON (API format) */
  workflow: Record<string, unknown>;
};

type ComfyUIImageBinding<TParams extends OperationParamsSchema> = {
  provider: "comfyui";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): ComfyUIImagePayload;
};

// ElevenLabs TTS binding
export type ElevenLabsTTSPayload = {
  text: string;
  voiceId: string;
  modelId?: string;
  stability?: number;
  similarityBoost?: number;
};

type ElevenLabsTTSBinding<TParams extends OperationParamsSchema> = {
  provider: "elevenlabs";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): ElevenLabsTTSPayload;
};

// fal.ai/MiniMax TTS binding. Used by minimax-speech-2-8-hd which renders
// with cloned voices created via wonda voice create.
export type FalMinimaxTTSPayload = {
  prompt: string;
  voiceId: string;
  languageBoost?: string;
};

type FalMinimaxTTSBinding<TParams extends OperationParamsSchema> = {
  provider: "fal";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): FalMinimaxTTSPayload;
};

// ElevenLabs STT binding
export type ElevenLabsSTTPayload = {
  modelId?: string;
  languageCode?: string;
};

type ElevenLabsSTTBinding<TParams extends OperationParamsSchema> = {
  provider: "elevenlabs";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): ElevenLabsSTTPayload;
};

// ElevenLabs Dialogue binding
export type ElevenLabsDialoguePayload = {
  inputs: { text: string; voiceId: string }[];
  modelId?: string;
  stability?: number;
};

type ElevenLabsDialogueBinding<TParams extends OperationParamsSchema> = {
  provider: "elevenlabs";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): ElevenLabsDialoguePayload;
};

// ElevenLabs Alignment binding
export type ElevenLabsAlignmentPayload = {
  text: string;
};

type ElevenLabsAlignmentBinding<TParams extends OperationParamsSchema> = {
  provider: "elevenlabs";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): ElevenLabsAlignmentPayload;
};

// New: OpenRouter text binding
type OpenRouterTextBinding<TParams extends OperationParamsSchema> = {
  provider: "openrouter";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): {
    prompt: string;
    llm?: string;
    systemPrompt?: string;
    imageUrls?: string[]; // Optional image URLs for multimodal vision models
  };
};

// Kie binding for Suno music generation
type KieSunoBinding<TParams extends OperationParamsSchema> = {
  provider: "kie";
  modelId: "suno";
  /** Return microdollars, same convention as others. */
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): SunoGeneratePayload;
};

// Replicate audio-enhance / voice-extract bindings.
// These wrap provider-side Replicate model versions; the dispatcher in
// runInference reads `audioUrl` from resolved attachments.
export type ReplicateAudioEnhancePayload = {
  solver?: string;
  nfe?: number;
  chunkSeconds?: number;
  chunksOverlap?: number;
  denoise?: boolean;
};

type ReplicateAudioEnhanceBinding<TParams extends OperationParamsSchema> = {
  provider: "replicate";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): ReplicateAudioEnhancePayload;
};

export type ReplicateVoiceExtractPayload = {
  model?: string;
};

type ReplicateVoiceExtractBinding<TParams extends OperationParamsSchema> = {
  provider: "replicate";
  modelId: string;
  estimateCost(p: BaseParams & z.infer<TParams>): number;
  toPayload(p: BaseParams & z.infer<TParams>): ReplicateVoiceExtractPayload;
};

export type ProviderBinding<
  TStep extends StepType,
  TParams extends OperationParamsSchema,
> = TStep extends "image"
  ?
      | RunwareImageBinding<TParams>
      | RunwareVectorizeBinding<TParams>
      | FalImageBinding<TParams>
      | KieImageBinding<TParams>
      | XaiImageBinding<TParams>
      | ComfyUIImageBinding<TParams>
      | OpenAiImageBinding<TParams>
  : TStep extends "video"
    ?
        | RunwareVideoBinding<TParams>
        | OpenAiSoraVideoBinding<TParams>
        | FalVideoBinding<TParams>
        | KieVideoBinding<TParams>
        | MuapiVideoBinding<TParams>
        | XaiVideoBinding<TParams>
        | KlingDirectVideoBinding<TParams>
        | VertexVideoBinding<TParams>
        | ReplicateVideoBinding<TParams>
    : TStep extends "tts"
      ? ElevenLabsTTSBinding<TParams> | FalMinimaxTTSBinding<TParams>
      : TStep extends "stt"
        ? ElevenLabsSTTBinding<TParams>
        : TStep extends "text"
          ? OpenRouterTextBinding<TParams>
          : TStep extends "dialogue"
            ? ElevenLabsDialogueBinding<TParams>
            : TStep extends "alignment"
              ? ElevenLabsAlignmentBinding<TParams>
              : TStep extends "music"
                ? KieSunoBinding<TParams>
                : TStep extends "audio-enhance"
                  ? ReplicateAudioEnhanceBinding<TParams>
                  : TStep extends "voice-extract"
                    ? ReplicateVoiceExtractBinding<TParams>
                    : never;

export type BaseParams = {
  prompt: string;
};

// ---------- Reference pricing (competitor comparison) ----------

export type ReferencePricingEntry = {
  unit: string;
  unitPriceMicrodollars: number;
  variant?: string;
};

export type ReferencePricing = {
  provider: string;
  url?: string;
  entries: ReferencePricingEntry[];
};

export type StepModelBase<
  TParams extends OperationParamsSchema = OperationParamsSchema,
  TFeatures extends StepFeatureMap = StepFeatureMap,
> = {
  label: string;
  description: string;
  icon?: string;
  tags: string[];
  featured?: boolean;
  /** Hide this model from UI dropdowns (existing nodes still work) */
  hidden?: boolean;
  features?: TFeatures;
  options?: StepOptions<TFeatures>;
  params: TParams;
  info?: string;
};

export type ModelSpecNode<
  TParams extends OperationParamsSchema = OperationParamsSchema,
  TFeatures extends StepFeatureMap = StepFeatureMap,
  TStep extends StepType = StepType,
> = {
  key: string;
  stepType: TStep;
  spec: StepModelBase<TParams, TFeatures>;
  providers: ProviderBinding<TStep, TParams>[];
  referencePricing?: ReferencePricing;
};

// keep your helper as-is
export function defineModel<
  P extends OperationParamsSchema,
  F extends StepFeatureMap,
  S extends StepType,
>(modelSpec: {
  key: string;
  stepType: S;
  spec: StepModelBase<P, F>;
  providers: ProviderBinding<S, P>[];
  referencePricing?: ReferencePricing;
}): ModelSpecNode<P, F, S> {
  return modelSpec as unknown as ModelSpecNode<P, F, S>;
}
